import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import requests from "../requests";
import { TOUR_EVENT } from "./WelcomeModal";
import { useSeo } from "../hooks/useSeo";

// Coverage/scale only. /api/stats stopped sending realized accuracy fields on 2026-07-31
// (win rates, avg returns, resolved_signals, diagnostics) — do not re-add them here.
interface SignalStats {
  // Optional: /api/stats can 200 with a partial/empty body before the caches load,
  // so these always-rendered counts are guarded with `?? 0` at the call sites.
  total_patterns_detected?: number;
  tickers_covered?: number;
  years_of_data?: number;
  // Total analog vectors in the retrieval pool (active + delisted, all windows) —
  // a scale claim, not an accuracy claim (see resources/stats.py).
  total_analog_vectors?: number;
  delisted_analog_vectors?: number;
  data_as_of?: string | null;
}

// Compact "56M+" style formatting for a live vector count. The fallback is a floor the
// library has cleared since 2026-09, shown only until /api/stats resolves.
const formatVectorCount = (n?: number): string => {
  if (!n) return "50M+";
  if (n >= 1_000_000) return `${Math.floor(n / 1_000_000)}M+`;
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K+`;
  return `${n}+`;
};

// Mirrors the API's SLICE_PARAMS (window -> committed forecast horizon). Keep in sync.
const WINDOWS = [
  { name: "2 weeks", days: 10, horizon: "1 day" },
  { name: "4 weeks", days: 20, horizon: "3 days" },
  { name: "3 months", days: 65, horizon: "5 days" },
  { name: "6 months", days: 130, horizon: "10 days" },
  { name: "1 year", days: 260, horizon: "20 days" },
];

const bodyText = { color: "var(--text-light)", lineHeight: 1.7 };

// Copy rule for this page (2026-10-14): describe what the engine does, never what it will
// make you. No "predictions", "probability", "edge", "AI/ML" or accuracy claims — the
// 2026-07-31 audit found no directional edge clearing the matched null.
export default function About() {
  useSeo({
    title: "How DejaChart Works — Methodology",
    description:
      "How DejaChart finds historical chart analogs: shape-normalized price windows, a cosine search re-ranked with dynamic time warping over 20 years of data including delisted stocks, and what happened next.",
    path: "/about",
  });
  const [openAccordion, setOpenAccordion] = React.useState("1");
  const [stats, setStats] = useState<SignalStats | null>(null);
  const loggedIn = useSelector((state: RootReducerState) => state.authentication.loggedIn);

  useEffect(() => {
    requests.get("/api/stats")
      .then(res => setStats(res.data))
      .catch(() => {/* degrade silently */});
  }, []);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? "" : id);
  };

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <Container>
          <Row className="align-items-center">
            <Col lg="8" className="mx-auto text-center">
              <h1 className="hero-title">
                Find the charts that looked like this one, and see what happened next
              </h1>
              <p className="hero-subtitle">
                DejaChart compares a stock&apos;s recent price shape with 20 years of daily
                history, including companies that no longer trade. It shows you the closest
                historical analogs and how each one played out. It is a research tool, not a
                forecast.
              </p>
              <div className="hero-badges">
                <Badge color="primary" pill className="feature-badge">
                  20 years of daily data
                </Badge>
                <Badge color="success" pill className="feature-badge">
                  {formatVectorCount(stats?.total_analog_vectors)} historical windows
                </Badge>
                <Badge color="info" pill className="feature-badge">
                  Updated every trading night
                </Badge>
              </div>
              <div style={{ marginTop: "1.5rem" }}>
                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={() => window.dispatchEvent(new Event(TOUR_EVENT))}
                >
                  Take the product tour
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How it works */}
      <section className="how-it-works-section">
        <Container>
          <div className="section-header">
            <h2>How it works</h2>
            <p>Three steps, run for every ticker every night and on demand in the Query Matcher</p>
          </div>

          <Row className="process-steps">
            <Col lg="4" md="6" className="mb-4">
              <Card className="process-card h-100">
                <CardBody>
                  <div className="process-icon data-icon">
                    <span className="icon-number">1</span>
                  </div>
                  <h3>Turn a chart into a shape</h3>
                  <ul className="feature-list">
                    <li>
                      <strong>Five lookback windows:</strong> 2 weeks, 4 weeks, 3 months,
                      6 months and 1 year (10 to 260 trading days).
                    </li>
                    <li>
                      <strong>Shape, not price:</strong> each window&apos;s closing prices are
                      normalized, so a $5 stock and a $500 stock that moved the same way look
                      the same.
                    </li>
                    <li>
                      <strong>The library:</strong> every covered security, sliced into a
                      window starting every third trading day across 20 years.
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>

            <Col lg="4" md="6" className="mb-4">
              <Card className="process-card h-100">
                <CardBody>
                  <div className="process-icon algorithm-icon">
                    <span className="icon-number">2</span>
                  </div>
                  <h3>Find the closest analogs</h3>
                  <ul className="feature-list">
                    <li>
                      <strong>Wide net:</strong> an exact cosine-similarity search pulls the
                      5,000 most similar historical windows.
                    </li>
                    <li>
                      <strong>Careful re-rank:</strong> dynamic time warping (DTW) re-scores
                      those candidates, so two charts that make the same move a few days apart
                      still line up.
                    </li>
                    <li>
                      <strong>Fair comparison:</strong> analogs with a similar beta to the stock
                      are preferred, and every analog ends before today, so its outcome is
                      already history. Up to 100 are kept.
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>

            <Col lg="4" md="6" className="mb-4">
              <Card className="process-card h-100">
                <CardBody>
                  <div className="process-icon prediction-icon">
                    <span className="icon-number">3</span>
                  </div>
                  <h3>Show what happened next</h3>
                  <ul className="feature-list">
                    <li>
                      <strong>A fixed horizon per window:</strong>{" "}
                      {WINDOWS.map((w) => `${w.horizon} after a ${w.name} window`).join(", ")}.
                    </li>
                    <li>
                      <strong>The whole spread:</strong> how many analogs rose and fell, the
                      typical size of each, the range of outcomes, and a day-by-day forward
                      curve.
                    </li>
                    <li>
                      <strong>Every analog on show:</strong> each one is listed with its
                      ticker, dates and chart, so you can judge the match yourself.
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Reading a signal */}
      <section className="how-it-works-section" style={{ backgroundColor: "var(--gray-100)" }}>
        <Container>
          <div className="section-header">
            <h2>Reading a signal</h2>
            <p>What the numbers describe, and what they don&apos;t</p>
          </div>

          <Row className="align-items-start g-4">
            <Col lg="6">
              <Card className="process-card h-100">
                <CardBody>
                  <h3 style={{ marginBottom: "1rem" }}>How a ticker makes Featured</h3>
                  <p style={{ ...bodyText, marginBottom: "1rem" }}>
                    Each night, every ticker in the signal universe is searched in all five
                    windows. A ticker is featured in a window when it has at least 10 analogs
                    above that window&apos;s similarity bar and more than 60% of them moved the
                    same way. The top 10 in each direction are listed, ranked by how strongly
                    and how far their analogs moved.
                  </p>
                  <p style={bodyText}>
                    Ten analogs is a small sample. Six of ten rising is weaker evidence than
                    sixty of a hundred, so always read the count next to the percentage.
                    Tickers that aren&apos;t featured still get a full readout on their own
                    page.
                  </p>
                </CardBody>
              </Card>
            </Col>

            <Col lg="6">
              <Card className="process-card h-100">
                <CardBody>
                  <h3 style={{ marginBottom: "1rem" }}>What the numbers mean</h3>
                  <ul className="feature-list">
                    <li>
                      <strong>% up / % down:</strong> the share of analogs that rose or fell
                      over the window&apos;s horizon. It describes the analogs. It is not a
                      probability for this stock.
                    </li>
                    <li>
                      <strong>Avg up / avg down:</strong> the typical size of the analogs&apos;
                      gains and of their losses, reported separately.
                    </li>
                    <li>
                      <strong>Analogs:</strong> how many historical windows the numbers rest on,
                      and how closely they matched.
                    </li>
                    <li>
                      <strong>Delisted share:</strong> how many analogs came from securities
                      that have since delisted. A high share is flagged on the card.
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Coverage & scale. This section used to headline a realized win rate from the
          track record; that was withdrawn 2026-07-31 after the audit found no directional
          edge clearing the matched null, and /api/stats no longer sends accuracy numbers.
          Do not reintroduce a win rate here — see the API's resources/track_record.py. */}
      {stats && (
        <section style={{ padding: "3rem 0", background: "var(--background-secondary)" }}>
          <Container>
            <div className="section-header">
              <h2>Coverage</h2>
              <p>
                Signals are generated for Russell 3000 stocks, a set of major ETFs, futures,
                FX and crypto. Every security we hold data for, including delisted ones,
                serves as an analog.
                {stats.data_as_of && <> Data as of {stats.data_as_of}.</>}
              </p>
            </div>
            <Row className="g-3 text-center justify-content-center">
              {[
                { value: `${(stats.tickers_covered ?? 0).toLocaleString()}+`, label: "Tickers covered" },
                { value: `${stats.years_of_data ?? 0} yrs`, label: "Daily history" },
                { value: formatVectorCount(stats.total_analog_vectors), label: "Historical windows" },
                ...(stats.delisted_analog_vectors
                  ? [{ value: formatVectorCount(stats.delisted_analog_vectors), label: "From delisted securities" }]
                  : []),
                { value: (stats.total_patterns_detected ?? 0).toLocaleString(), label: "Chart patterns detected" },
              ].map((s) => (
                <Col key={s.label} md="2" xs="4">
                  <div className="stat-item">
                    <div className="stat-number" style={{ fontSize: "1.6rem" }}>{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* FAQ */}
      <section className="faq-section" id="faq">
        <Container>
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>The methodology in plain terms</p>
          </div>

          <Row>
            <Col lg="10" className="mx-auto">
              <Accordion
                open={openAccordion}
                toggle={toggleAccordion}
                className="faq-accordion"
              >
                <AccordionItem>
                  <AccordionHeader targetId="1">Is this a prediction?</AccordionHeader>
                  <AccordionBody accordionId="1">
                    No. An analog set tells you how similar-looking charts resolved in the
                    past. Markets change, and a shape that rose eight times out of ten can fall
                    the ninth. We don&apos;t publish an overall accuracy number, and these
                    signals aren&apos;t sold as forecasts. Use them as context alongside your
                    own research, not as an instruction to buy or sell.
                  </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                  <AccordionHeader targetId="2">What exactly gets compared?</AccordionHeader>
                  <AccordionBody accordionId="2">
                    The shape of the closing-price series over the window, normalized so the
                    price level doesn&apos;t matter. We tested adding volume and intraday range
                    to the comparison. Both made the analogs worse, so they are left out.
                  </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                  <AccordionHeader targetId="3">Why dynamic time warping?</AccordionHeader>
                  <AccordionBody accordionId="3">
                    Two charts can make the same move with the turn a few days apart. A plain
                    point-by-point comparison scores that as a poor match. DTW aligns the two
                    series before scoring them. It is slower than a plain comparison, so a fast
                    cosine search narrows millions of windows to 5,000 candidates first, and
                    DTW ranks those.
                  </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                  <AccordionHeader targetId="4">Why include delisted stocks?</AccordionHeader>
                  <AccordionBody accordionId="4">
                    If only today&apos;s survivors could be analogs, history would look kinder
                    than it was: the companies that collapsed would be missing from every
                    outcome. Roughly half of the library comes from securities that have since
                    delisted, and each card shows how many of its analogs did.
                  </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                  <AccordionHeader targetId="5">How often is it updated?</AccordionHeader>
                  <AccordionBody accordionId="5">
                    Every trading night, after the day&apos;s closing prices arrive. Featured
                    signals, chart patterns and rankings are rebuilt each night. The Query
                    Matcher runs a fresh search whenever you ask it to. Nothing here is
                    intraday.
                  </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                  <AccordionHeader targetId="6">What are the classic chart patterns?</AccordionHeader>
                  <AccordionBody accordionId="6">
                    A separate nightly scan for 22 textbook formations, such as head and
                    shoulders, double tops and cup and handle, over windows from 1 month to 1
                    year. Each detection shows how well the chart fits the template.
                  </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                  <AccordionHeader targetId="7">Who is it for?</AccordionHeader>
                  <AccordionBody accordionId="7">
                    Traders and investors who want historical context before they act. The
                    horizons are short, from 1 to 20 trading days, so it suits swing trading
                    better than long-term investing.
                  </AccordionBody>
                </AccordionItem>

                <AccordionItem>
                  <AccordionHeader targetId="8">What does it cost?</AccordionHeader>
                  <AccordionBody accordionId="8">
                    Every signal in every window is free, at the same time for everyone. A free
                    account unlocks the full lists and 20 Query Matcher searches a day. Pro adds
                    unlimited searches, custom alerts, a personalized feed and a daily email
                    digest. See <Link to="/pricing">Pricing</Link>.
                  </AccordionBody>
                </AccordionItem>
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="disclaimer-section">
        <Container>
          <div className="section-header">
            <h2>Disclaimer</h2>
            <p className="disclaimer-text">
              The information and data provided on this website are for informational
              and educational purposes only. Nothing on this website constitutes
              investment, financial, legal, or other professional advice.
            </p>
            <br/>
            <p className="disclaimer-text">
              DejaChart does not make any representations or warranties regarding the
              accuracy, completeness, or timeliness of the information provided.
              Market data and other information may be delayed or incomplete. Any
              reliance you place on such information is strictly at your own risk.
            </p>
            <br/>
            <p className="disclaimer-text">
              Nothing contained on this website constitutes an offer, recommendation,
              or solicitation to buy or sell any securities, financial instruments, or
              other investments. The content on this site should not be interpreted as
              investment advice from DejaChart or any of its employees, affiliates, or
              representatives.
            </p>
            <br/>
            <p className="disclaimer-text">
              Past performance is not indicative of future results.
            </p>
            <br/>
            <p className="disclaimer-text">
              DejaChart and its employees are not registered investment advisors.
              Users should conduct their own research and consult with a qualified
              financial advisor before making any investment decisions.
            </p>
            <br/>
            <p className="disclaimer-text">
              DejaChart and its employees shall not be held liable for any losses or
              damages arising from the use of the information provided on this website.
            </p>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <Container>
          <Row className="text-center">
            <Col lg="8" className="mx-auto">
              <h2>See what today&apos;s charts looked like before</h2>
              <p>
                Browse tonight&apos;s featured analogs, or search any ticker in the Query
                Matcher.
              </p>
              <div className="cta-buttons">
                <Link to="/featured" className="btn btn-primary btn-lg">
                  Browse today&apos;s signals
                </Link>
                {loggedIn ? (
                  <Link to="/dashboard" className="btn btn-outline-primary btn-lg">
                    Open the Query Matcher
                  </Link>
                ) : (
                  <Link to="/signup" className="btn btn-outline-primary btn-lg">
                    Create a free account
                  </Link>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
}
